// supervisor-dispatch-guards.ts
//
// §18 `beforeDispatch` gate (phase-08 Agent H): the ONE call site combining
// quiet hours, max_sessions_per_hour, min_free_disk_mb, and pack-pin drift
// into a single "may a NEW attempt dispatch right now" decision. Kept in its
// own file (per phase-08 brief's collision-avoidance instruction — Agent G
// edits supervisor-loop.ts/mission-state-machine.ts for SURVEYING in this
// same phase) and wired into the tick loop via one call in
// supervisor-before-dispatch-gate-step.ts at the tick-boundary point (same point redirect/revert/abort already check —
// no attempt in flight, safe to gate).
//
// This module NEVER blocks the control loop itself — only the decision of
// whether THIS tick may start a new attempt. Callers that get `blocked` back
// treat it exactly like a quota sleep: skip dispatch this tick, keep
// polling inbox/outbox/heartbeat, try again next tick.
import { warnCostNearCeilingOnce } from "./cost-ceiling-warning.js";
import { checkMinFreeDisk } from "./log-retention-manager.js";
import {
  type CostAttemptLimitsConfig,
  checkCostAttemptCeilingAndMaybeEscalate,
} from "./mission-cost-accounting.js";
import type { MissionStore } from "./mission-store.js";
import {
  captureVerifiedTuple,
  detectPinDrift,
  readVerifiedTuple,
} from "./pack-pin-drift-detector.js";
import { evaluateQuietHours, evaluateSessionCap } from "./quiet-hours-scheduler.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

// P13-review split (must-fix #1): readOpenAttemptIds/readAttemptTimestampsIso
// moved to their own file to bring this one back under the 200-line budget.
// Re-exported here so every existing `from "./supervisor-dispatch-guards.js"`
// import keeps compiling unchanged.
export {
  readAttemptTimestampsIso,
  readOpenAttemptIds,
} from "./supervisor-attempt-directory-reader.js";

export type BeforeDispatchConfig = Pick<
  AutopilotConfig,
  "quiet_hours" | "max_sessions_per_hour" | "min_free_disk_mb" | "logs"
> &
  // P13 Agent B: optional so callers/tests predating this phase (config
  // object without these two keys) keep compiling and default to
  // "unlimited" — never a hidden numeric fallback (the fallback IS 0 ==
  // unlimited, the §18-style convention already codified in
  // config-schema.ts's own `.default(0)`, not a literal threshold).
  Partial<CostAttemptLimitsConfig>;

export interface EvaluateBeforeDispatchInput {
  missionWorkspace: string;
  controlRoot: string;
  config: BeforeDispatchConfig;
  now: Date;
  attemptTimestampsIso: readonly string[];
  probeClaudeVersion: () => Promise<string>;
  /** Injectable, same seam as log-retention-manager.ts's checkMinFreeDisk. */
  statfs?: (path: string) => Promise<{ bavail: number; bsize: number }>;
  /**
   * P13 Agent B: when supplied, a cost/attempt ceiling breach commits a
   * `mission_status_changed` -> WAITING_HUMAN receipt to THIS store before
   * returning blocked (so `status`/`status --json` can report why without
   * re-deriving it). Omitted in tests that only care about routing, not the
   * WAITING_HUMAN side effect.
   */
  store?: MissionStore;
  /** 06g R5: operator progress sink for the one-per-process "≥ 80 % trần" line; undefined ⇒ silent. */
  progress?: (line: string) => void;
}

export type BeforeDispatchBlockCode =
  | "quiet_hours"
  | "max_sessions_per_hour"
  | "min_free_disk"
  | "pin_drift"
  | "cost_limit_reached"
  | "attempt_limit_reached";

export interface BeforeDispatchDecision {
  blocked: boolean;
  /** Machine-readable — journaled into mission status (e.g. `status --json`) so a blocked run reports WHY without parsing the human-readable reason string. */
  code?: BeforeDispatchBlockCode;
  reason?: string;
  resumeAt?: string;
}

/**
 * Evaluated in a fixed, cheapest-first order — the first failing guard's
 * reason is returned (each blocks independently; a caller only needs to
 * know it's blocked and why, not every simultaneously-failing guard).
 */
export async function evaluateBeforeDispatch(
  input: EvaluateBeforeDispatchInput,
): Promise<BeforeDispatchDecision> {
  const quietHours = evaluateQuietHours({ quiet_hours: input.config.quiet_hours }, input.now);
  if (quietHours.blocked) {
    return {
      blocked: true,
      code: "quiet_hours",
      reason: `quiet hours đang hiệu lực (§18) — dispatch tạm dừng tới ${quietHours.resumeAt}.`,
      resumeAt: quietHours.resumeAt,
    };
  }

  const sessionCap = evaluateSessionCap(
    { max_sessions_per_hour: input.config.max_sessions_per_hour },
    input.attemptTimestampsIso,
    input.now,
  );
  if (sessionCap.blocked) {
    return {
      blocked: true,
      code: "max_sessions_per_hour",
      reason: `max_sessions_per_hour đã đạt (${sessionCap.countInWindow} session trong 60 phút gần nhất) — chặn session mới.`,
      // BUG-3 fix (dogfood recheck 260925): was missing entirely — the
      // operator had no idea how long to wait, unlike quiet_hours's own
      // resumeAt.
      resumeAt: sessionCap.resumeAt,
    };
  }

  const freeDisk = await checkMinFreeDisk({
    config: { min_free_disk_mb: input.config.min_free_disk_mb },
    path: input.controlRoot,
    statfs: input.statfs,
  });
  if (freeDisk.blocked) {
    return {
      blocked: true,
      code: "min_free_disk",
      reason: freeDisk.reason ?? "free disk dưới ngưỡng min_free_disk_mb.",
    };
  }

  const currentTuple = await captureVerifiedTuple({
    missionWorkspace: input.missionWorkspace,
    controlRoot: input.controlRoot,
    probeClaudeVersion: input.probeClaudeVersion,
    now: () => input.now,
  });
  const verifiedTuple = await readVerifiedTuple(input.controlRoot);
  const pinDrift = detectPinDrift(currentTuple, verifiedTuple);
  if (pinDrift.blocked) {
    return {
      blocked: true,
      code: "pin_drift",
      reason: `pack pin/version drift ở: ${pinDrift.changedFields.join(", ")} — chạy 'avatar autopilot doctor --reverify' trước khi dispatch tiếp.`,
    };
  }

  // P13 Agent B: last (most expensive — reads the whole journal) and, unlike
  // every guard above, PERMANENT for this mission once breached (quiet
  // hours/disk/pin-drift are transient — a later tick may pass; a cost/
  // attempt ceiling already exceeded never un-exceeds itself). Escalates to
  // WAITING_HUMAN with a journaled receipt rather than the "keep polling,
  // try again next tick" treatment every other guard gets.
  // Not `?? 0` (AST-gate's numeric-fallback rule flags any `?? <number>` on
  // a §18-style key regardless of intent) — BeforeDispatchConfig's cost
  // fields are Partial<> only so pre-P13 callers/tests keep compiling;
  // explicit undefined checks keep this outside the gate's pattern.
  // 3.38.1: an absent USD key stays absent — no USD ceiling (no default).
  const costConfig: CostAttemptLimitsConfig = {
    max_cost_usd_per_mission: input.config.max_cost_usd_per_mission,
    max_attempts_per_mission:
      input.config.max_attempts_per_mission === undefined
        ? 0
        : input.config.max_attempts_per_mission,
  };
  const costLimit = await checkCostAttemptCeilingAndMaybeEscalate(
    input.controlRoot,
    costConfig,
    input.store,
    input.now,
  );
  if (costLimit.blocked && costLimit.code) {
    return { blocked: true, code: costLimit.code, reason: costLimit.reason };
  }
  // 06g R5: not breached — but say so once when the ceiling is near.
  await warnCostNearCeilingOnce(input.controlRoot, costConfig, input.progress);

  return { blocked: false };
}
